/**
 * PromptBuilder - Unified prompt generation for meal plans and alternative recipes
 * 
 * This class encapsulates all shared logic for building prompts:
 * - Cuisine constraints (single and multi-cuisine modes)
 * - Ingredient hints based on budget tier and cuisine kits
 * - Dietary filtering and swap examples
 * - Recipe inspirations
 * - Cuisine distribution (Surprise Me mode)
 * 
 * Usage:
 *   const builder = new PromptBuilder({
 *     country: 'USA',
 *     selectedCuisine: 'Italian',
 *     dietaryPreferences: [{key: 'noDairy', label: 'Dairy Free'}],
 *     budgetTier: 'standard',
 *     numberOfPeople: 4
 *   });
 *   
 *   const constraints = builder.getCuisineConstraints();
 *   const hints = builder.getIngredientHints();
 *   const inspirations = builder.getInspirations('dinner');
 * 
 * @module PromptBuilder
 */

import countryFoodPreferences from './country_food_preferences.json';
import inspirationsData from './inspirations.json';
import { filterByRestrictions, shouldExcludeDairy, getForbiddenIngredientsText } from './dietaryFilters';
import { COOKING_TIME_LIMITS, getPassiveCookingBonus } from './cookingTimeLimits';

/**
 * PromptBuilder class for unified prompt generation logic
 */
export class PromptBuilder {
  /**
   * Create a PromptBuilder instance
   * @param {Object} config - Configuration object
   * @param {string} config.country - Country code (e.g., 'USA', 'Spain')
   * @param {string} [config.selectedCuisine='Surprise Me'] - Selected cuisine(s) or 'Surprise Me'
   * @param {Array|string} [config.dietaryPreferences=[]] - Dietary preferences
   * @param {string} [config.budgetTier='standard'] - Budget tier (budget, standard, gourmet)
   * @param {number} [config.numberOfPeople=2] - Number of people
   * @param {number} [config.totalMeals=9] - Total meals in the plan
   * @param {Object} [config.cookingStyle=null] - Cooking style per meal type { breakfast: { cookingTime, cookFor2Days }, ... }
   */
  constructor(config = {}) {
    this.country = config.country || 'USA';
    this.selectedCuisine = config.selectedCuisine || 'Surprise Me';
    this.dietaryPreferences = config.dietaryPreferences || [];
    this.budgetTier = config.budgetTier || 'standard';
    this.numberOfPeople = config.numberOfPeople || 2;
    this.totalMeals = config.totalMeals || 9;
    this.cookingStyle = config.cookingStyle || null;
    
    // Derived state
    this.countryData = countryFoodPreferences[this.country] || countryFoodPreferences['USA'];
    this.restrictionKeys = this._extractRestrictionKeys(this.dietaryPreferences);
    this.parsedCuisines = this._parseCuisines(this.selectedCuisine);
    this.isSingleCuisine = this.parsedCuisines.length === 1 && this.selectedCuisine !== 'Surprise Me';
    this.isMultiCuisine = this.parsedCuisines.length > 1;
    this.isSurpriseMe = this.selectedCuisine === 'Surprise Me';
    
    // Cooking time limits — imported from the shared single source of truth
    // (src/utils/cookingTimeLimits.js) so the alternatives prompt can never
    // drift from the meal-plan prompt + runtime validator.
    this.COOKING_TIME_LIMITS = COOKING_TIME_LIMITS;
    
    // Cached results
    this._cuisineKitCache = null;
    this._constraintsCache = null;
  }

  // ─────────────────────────────────────────────────────────────────
  // PRIVATE HELPERS
  // ─────────────────────────────────────────────────────────────────

  /**
   * Parse selected cuisine string into array
   * @private
   */
  _parseCuisines(selectedCuisine) {
    if (!selectedCuisine || selectedCuisine === 'Surprise Me') return [];
    return selectedCuisine.split(',').map(c => c.trim()).filter(Boolean);
  }

  /**
   * Extract restriction keys from dietary preferences (handles both string and array formats)
   * @private
   */
  _extractRestrictionKeys(dietaryPreferences) {
    if (!dietaryPreferences) return [];
    
    if (typeof dietaryPreferences === 'string') {
      return dietaryPreferences.toLowerCase().includes('none') ? [] : [dietaryPreferences];
    }
    
    if (Array.isArray(dietaryPreferences)) {
      return dietaryPreferences
        .filter(pref => pref && pref.key && pref.key !== 'none')
        .map(pref => pref.key);
    }
    
    return [];
  }

  /**
   * Format dietary preferences for display in prompts
   * @private
   */
  _formatDietaryPreferences(dietaryPreferences) {
    if (typeof dietaryPreferences === 'string') {
      return dietaryPreferences;
    }
    
    if (Array.isArray(dietaryPreferences)) {
      const activePreferences = dietaryPreferences.filter(pref => 
        pref && pref.key && pref.key !== 'none'
      );
      
      if (activePreferences.length === 0) return 'None';
      
      return activePreferences.map(pref => 
        `${pref.label} (${pref.description})`
      ).join(', ');
    }
    
    return 'None';
  }

  /**
   * Get random items from an array
   * @private
   */
  _getRandomItems(arr, count = 2) {
    if (!arr || arr.length === 0) return [];
    const shuffled = [...arr].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(count, arr.length));
  }

  /**
   * Apply cuisine kit filtering to a tier list:
   * removes items in avoid list, boosts anchors to front
   * @private
   */
  _applyCuisineKitToTier(arr, cuisineAnchorsArr = [], cuisineAvoidArr = []) {
    if (!arr?.length) return [];
    
    const avoid = new Set((cuisineAvoidArr || []).map(s => s.toLowerCase()));
    const anchors = new Set((cuisineAnchorsArr || []).map(s => s.toLowerCase()));

    // Remove items that are in the avoid list
    const filtered = arr.filter(x => !avoid.has(String(x).toLowerCase()));

    // Stable sort: anchors first, then rest
    const anchored = [];
    const rest = [];
    for (const x of filtered) {
      (anchors.has(String(x).toLowerCase()) ? anchored : rest).push(x);
    }
    return [...anchored, ...rest];
  }

  /**
   * Get the active cuisine kit (for single cuisine mode)
   * @private
   */
  _getActiveCuisineKit() {
    if (this._cuisineKitCache !== null) return this._cuisineKitCache;
    
    if (this.isSingleCuisine && this.countryData?.cuisineKits) {
      this._cuisineKitCache = this.countryData.cuisineKits[this.parsedCuisines[0]] || null;
    } else {
      this._cuisineKitCache = null;
    }
    
    return this._cuisineKitCache;
  }

  // ─────────────────────────────────────────────────────────────────
  // PUBLIC API: CUISINE CONSTRAINTS
  // ─────────────────────────────────────────────────────────────────

  /**
   * Build cuisine hard constraint block for single cuisine mode
   * @param {Object} [safeInspirations] - Optional filtered inspirations object
   * @returns {Object} { isSingleCuisine, hardConstraintBlock, inspirationsAreHard, formatExamples, minArchetypeMeals }
   */
  getSingleCuisineConstraint(safeInspirations = null) {
    if (!this.isSingleCuisine) {
      return { isSingleCuisine: false, hardConstraintBlock: '', inspirationsAreHard: false, formatExamples: null };
    }
    
    const cuisineName = this.parsedCuisines[0];
    const constraints = this.countryData?.cuisineHardConstraints?.[cuisineName];
    
    if (!constraints) {
      // Fallback for cuisines without specific constraints
      return {
        isSingleCuisine: true,
        hardConstraintBlock: `
   SINGLE-CUISINE HARD CONSTRAINT (${cuisineName}):
   - HARD: All ${this.totalMeals} meals MUST be recognizably ${cuisineName} home cooking.
   - HARD: Do NOT generate generic European or fusion dishes.
   - HARD: Dish titles and descriptions must reflect ${cuisineName} culinary traditions.
   - HARD: All ${this.totalMeals} meals MUST be derived from the inspirations lists below. Do not invent new formats.`,
        inspirationsAreHard: true,
        formatExamples: null
      };
    }
    
    const archetypesList = constraints.archetypes.slice(0, 10).join(', ');
    const flavorAnchorsList = constraints.flavorAnchors.join(', ');
    const hardAvoidList = constraints.hardAvoid.slice(0, 15).join(', ');
    
    return {
      isSingleCuisine: true,
      hardConstraintBlock: `
   SINGLE-CUISINE HARD CONSTRAINT (${cuisineName}):
   - HARD: All ${this.totalMeals} meals MUST be recognizably ${cuisineName} home cooking (not fusion or generic European).
   - HARD: All ${this.totalMeals} meals MUST be derived from the inspirations lists below. Do not invent new dish formats.
   - HARD: At least ${constraints.minArchetypeMeals}/${this.totalMeals} meals MUST be based on classic ${cuisineName} dish archetypes: ${archetypesList}.
   - HARD: Each meal MUST include at least 1-2 ${cuisineName} flavor anchors: ${flavorAnchorsList}.
   - HARD AVOID (wrong cuisine markers — do NOT use): ${hardAvoidList}.
   - Budget tier ingredient suggestions are SOFT and must only be used if they fit ${cuisineName} cuisine.`,
      inspirationsAreHard: true,
      minArchetypeMeals: constraints.minArchetypeMeals,
      formatExamples: constraints.formatExamples || null
    };
  }

  /**
   * Build multi-cuisine constraint block with quota distribution
   * @returns {Object} { isMultiCuisine, multiCuisineBlock, cuisineQuotas, cuisineCount }
   */
  getMultiCuisineConstraint() {
    if (!this.isMultiCuisine) {
      return { isMultiCuisine: false, multiCuisineBlock: '', cuisineQuotas: null };
    }
    
    const cuisineHardConstraints = this.countryData?.cuisineHardConstraints || {};
    
    // Calculate quota distribution
    const baseMealsPerCuisine = Math.floor(this.totalMeals / this.parsedCuisines.length);
    const remainder = this.totalMeals % this.parsedCuisines.length;
    
    const quotas = {};
    const quotaLines = [];
    
    this.parsedCuisines.forEach((cuisine, index) => {
      const mealCount = baseMealsPerCuisine + (index < remainder ? 1 : 0);
      quotas[cuisine] = mealCount;
      
      const constraints = cuisineHardConstraints[cuisine];
      const anchors = constraints?.flavorAnchors?.slice(0, 4).join(', ') || 'cuisine-appropriate ingredients';
      
      quotaLines.push(`   - ${cuisine}: exactly ${mealCount} meals (use ${cuisine} anchors: ${anchors})`);
    });
    
    // Build avoid list combining all cuisines' avoid lists, excluding ingredients from selected cuisines
    const selectedAnchors = new Set();
    this.parsedCuisines.forEach(cuisine => {
      const constraints = cuisineHardConstraints[cuisine];
      if (constraints?.flavorAnchors) {
        constraints.flavorAnchors.forEach(a => selectedAnchors.add(a.toLowerCase()));
      }
    });
    
    const combinedAvoid = new Set();
    this.parsedCuisines.forEach(cuisine => {
      const constraints = cuisineHardConstraints[cuisine];
      if (constraints?.hardAvoid) {
        constraints.hardAvoid.forEach(item => {
          if (!selectedAnchors.has(item.toLowerCase())) {
            combinedAvoid.add(item);
          }
        });
      }
    });
    
    const avoidList = Array.from(combinedAvoid).slice(0, 12).join(', ');
    
    return {
      isMultiCuisine: true,
      multiCuisineBlock: `
   MULTI-CUISINE DISTRIBUTION (${this.parsedCuisines.join(' + ')}):
   - HARD: Distribute meals evenly across selected cuisines:
${quotaLines.join('\n')}
   - Each meal must be clearly identifiable as ONE cuisine (no fusion).
   - Use cuisine-appropriate flavor anchors for each meal based on its assigned cuisine.
   - AVOID (not compatible with selected cuisines): ${avoidList}.`,
      cuisineQuotas: quotas,
      cuisineCount: this.parsedCuisines.length
    };
  }

  /**
   * Get unified cuisine constraints (single or multi-cuisine)
   * Convenience method that returns the appropriate constraint type
   * @param {Object} [safeInspirations] - Optional filtered inspirations
   * @returns {Object} Constraint object with type-specific properties
   */
  getCuisineConstraints(safeInspirations = null) {
    if (this.isSingleCuisine) {
      return this.getSingleCuisineConstraint(safeInspirations);
    } else if (this.isMultiCuisine) {
      return this.getMultiCuisineConstraint();
    }
    return { isSingleCuisine: false, isMultiCuisine: false, constraintBlock: '' };
  }

  /**
   * Build cuisine constraint for alternative recipes (compact version)
   * Used by AlternativeRecipes for replacement prompts
   * @param {string} mealType - Meal type being replaced
   * @returns {Object} { constraintBlock, flavorAnchors, hardAvoid }
   */
  getAlternativeCuisineConstraint(mealType = 'Dinner') {
    if (this.isSurpriseMe) {
      return { constraintBlock: '', flavorAnchors: [], hardAvoid: [] };
    }
    
    const cuisineHardConstraints = this.countryData?.cuisineHardConstraints || {};
    const cuisineKits = this.countryData?.cuisineKits || {};
    
    if (this.isSingleCuisine) {
      const cuisineName = this.parsedCuisines[0];
      const constraints = cuisineHardConstraints[cuisineName];
      const kit = cuisineKits[cuisineName];
      
      if (!constraints && !kit) {
        return { 
          constraintBlock: `All 3 alternatives MUST be recognizably ${cuisineName} cuisine.`,
          flavorAnchors: [],
          hardAvoid: [],
          allowedTitleNativeWords: []
        };
      }

      // Per-meal-type flavor anchors (fall back to global)
      const mealTypeKey = String(mealType || '').toLowerCase();
      const perMealAnchorKey =
        mealTypeKey === 'breakfast' ? 'breakfastFlavorAnchors' :
        mealTypeKey === 'lunch' ? 'lunchFlavorAnchors' :
        mealTypeKey === 'dinner' ? 'dinnerFlavorAnchors' : null;
      const perMealAnchors = perMealAnchorKey ? constraints?.[perMealAnchorKey] : null;
      const flavorAnchors = (perMealAnchors?.length ? perMealAnchors : null)
        || constraints?.flavorAnchors
        || kit?.flavorAnchors
        || [];

      // Per-meal-type archetypes (fall back to global archetypes)
      const perMealArchetypeKey =
        mealTypeKey === 'breakfast' ? 'breakfastArchetypes' :
        mealTypeKey === 'lunch' ? 'lunchArchetypes' :
        mealTypeKey === 'dinner' ? 'dinnerArchetypes' : null;
      const perMealArchetypes = perMealArchetypeKey ? constraints?.[perMealArchetypeKey] : null;
      const archetypesSource = (perMealArchetypes?.length ? perMealArchetypes : constraints?.archetypes) || [];
      const archetypes = archetypesSource.slice(0, 6).join(', ');

      // Per-meal-type formats (fall back to global formatExamples)
      const perMealFormatKey =
        mealTypeKey === 'breakfast' ? 'breakfastFormats' :
        mealTypeKey === 'lunch' ? 'lunchFormats' :
        mealTypeKey === 'dinner' ? 'dinnerFormats' : null;
      const perMealFormats = perMealFormatKey ? constraints?.[perMealFormatKey] : null;
      const formatsSource = (perMealFormats?.length ? perMealFormats : constraints?.formatExamples) || [];
      const formatExamples = formatsSource.slice(0, 4).join(', ');

      const hardAvoid = constraints?.hardAvoid || [];
      const allowedTitleNativeWords = constraints?.allowedTitleNativeWords || [];

      // Breakfast blacklist line — only when generating breakfast alternatives
      const breakfastBlacklist = (mealTypeKey === 'breakfast' && constraints?.breakfastBlacklist?.length)
        ? `\n- HARD BREAKFAST BLACKLIST (must NOT appear in any of the 3 alternatives): ${constraints.breakfastBlacklist.join(', ')}.`
        : '';

      return {
        constraintBlock: `
CUISINE HARD CONSTRAINT (${cuisineName}):
- HARD: All 3 alternatives MUST be recognizably ${cuisineName} home cooking (not fusion or generic).
- HARD: Each recipe MUST include at least 1-2 ${cuisineName} ${mealType.toLowerCase()} flavor anchors: ${flavorAnchors.slice(0, 5).join(', ')}.
${archetypes ? `- Preferred ${mealType.toLowerCase()} dish styles: ${archetypes}` : ''}
${formatExamples ? `- ${mealType} format examples: ${formatExamples}` : ''}
- HARD AVOID (wrong cuisine markers): ${hardAvoid.slice(0, 15).join(', ')}.${breakfastBlacklist}`,
        flavorAnchors,
        hardAvoid,
        allowedTitleNativeWords
      };
    }
    
    // Multi-cuisine mode
    const quotaLines = [];
    const allFlavorAnchors = [];
    const allHardAvoid = new Set();
    const selectedAnchorsSet = new Set();
    
    this.parsedCuisines.forEach((cuisine) => {
      const constraints = cuisineHardConstraints[cuisine];
      const kit = cuisineKits[cuisine];
      const anchors = constraints?.flavorAnchors || kit?.flavorAnchors || [];
      
      anchors.forEach(a => selectedAnchorsSet.add(a.toLowerCase()));
      allFlavorAnchors.push(...anchors);
      
      const avoidItems = constraints?.hardAvoid || [];
      avoidItems.forEach(item => {
        if (!selectedAnchorsSet.has(item.toLowerCase())) {
          allHardAvoid.add(item);
        }
      });
      
      quotaLines.push(`- At least 1 alternative should be ${cuisine} style (anchors: ${anchors.slice(0, 3).join(', ')})`);
    });
    
    return { 
      constraintBlock: `
MULTI-CUISINE CONSTRAINT (${this.parsedCuisines.join(' + ')}):
${quotaLines.join('\n')}
- Each recipe must be clearly ONE cuisine (no fusion).
- AVOID (not compatible): ${Array.from(allHardAvoid).slice(0, 10).join(', ')}.`,
      flavorAnchors: allFlavorAnchors, 
      hardAvoid: Array.from(allHardAvoid) 
    };
  }

  // ─────────────────────────────────────────────────────────────────
  // PUBLIC API: INGREDIENT HINTS
  // ─────────────────────────────────────────────────────────────────

  /**
   * Get ingredient hints for the current budget tier and cuisine
   * Applies dietary filtering and cuisine kit boosting/avoiding
   * @returns {string} Formatted ingredient hints string
   */
  getIngredientHints() {
    const tierKey = this._mapBudgetTier(this.budgetTier);
    const tier = this.countryData?.budgetTierIngredients?.[tierKey];
    
    if (!tier) return '';
    
    const excludeDairy = shouldExcludeDairy(this.restrictionKeys);
    const activeCuisineKit = this._getActiveCuisineKit();
    
    // Filter each category by dietary restrictions
    let filteredProteins = filterByRestrictions(tier.proteins, this.restrictionKeys);
    let filteredProduce = filterByRestrictions(tier.produce, this.restrictionKeys);
    let filteredDairy = excludeDairy ? [] : filterByRestrictions(tier.dairy, this.restrictionKeys);
    let filteredPantry = filterByRestrictions(tier.pantry, this.restrictionKeys);
    
    // If a single cuisine kit is active, apply cuisine filtering/reordering
    if (activeCuisineKit) {
      const anchors = activeCuisineKit.anchors || {};
      const avoid = activeCuisineKit.avoid || [];
      
      filteredProteins = this._applyCuisineKitToTier(filteredProteins, anchors.proteins, avoid);
      filteredProduce = this._applyCuisineKitToTier(filteredProduce, anchors.produce, avoid);
      filteredDairy = this._applyCuisineKitToTier(filteredDairy, anchors.dairy, avoid);
      filteredPantry = this._applyCuisineKitToTier(filteredPantry, anchors.pantry, avoid);
    }
    
    const parts = [];
    if (filteredProteins?.length) parts.push(`Proteins: ${filteredProteins.slice(0, 5).join(', ')}`);
    if (filteredProduce?.length) parts.push(`Produce: ${filteredProduce.slice(0, 5).join(', ')}`);
    if (filteredDairy?.length) parts.push(`Dairy: ${filteredDairy.slice(0, 4).join(', ')}`);
    if (filteredPantry?.length) parts.push(`Pantry: ${filteredPantry.slice(0, 4).join(', ')}`);
    if (tier.avoid?.length) parts.push(`AVOID: ${tier.avoid.join(', ')}`);
    
    return parts.length ? parts.join('. ') + '.' : '';
  }

  /**
   * Map various budget tier formats to standard tier key
   * @private
   */
  _mapBudgetTier(budgetTier) {
    const tierKeyMap = {
      survival: 'budget',
      budget: 'budget',
      standard: 'standard',
      gourmet: 'gourmet',
      luxury: 'gourmet'
    };
    return tierKeyMap[budgetTier?.toLowerCase()] || 'standard';
  }

  /**
   * Get cuisine anchors and avoid list for the selected cuisine(s)
   * @returns {Object} { cuisineAnchors, cuisineAvoid, flavorAnchors }
   */
  getCuisineKit() {
    let cuisineAnchors = null;
    let cuisineAvoid = [];
    let flavorAnchors = [];
    
    if (this.isSurpriseMe) {
      return { cuisineAnchors, cuisineAvoid, flavorAnchors };
    }
    
    if (this.isSingleCuisine) {
      const kit = this.countryData?.cuisineKits?.[this.parsedCuisines[0]];
      if (kit) {
        cuisineAnchors = kit.anchors;
        cuisineAvoid = filterByRestrictions(kit.avoid || [], this.restrictionKeys);
        flavorAnchors = filterByRestrictions(kit.flavorAnchors || [], this.restrictionKeys);
      }
    } else if (this.isMultiCuisine) {
      // Merge anchors from all selected cuisines
      const mergedAnchors = { proteins: [], produce: [], dairy: [], pantry: [] };
      const mergedFlavors = [];
      
      for (const cuisineName of this.parsedCuisines) {
        const kit = this.countryData?.cuisineKits?.[cuisineName];
        if (kit?.anchors) {
          for (const cat of ['proteins', 'produce', 'dairy', 'pantry']) {
            if (kit.anchors[cat]) {
              mergedAnchors[cat].push(...kit.anchors[cat]);
            }
          }
        }
        if (kit?.flavorAnchors) {
          mergedFlavors.push(...kit.flavorAnchors);
        }
      }
      
      // Dedupe
      for (const cat of Object.keys(mergedAnchors)) {
        mergedAnchors[cat] = [...new Set(mergedAnchors[cat])];
      }
      
      cuisineAnchors = mergedAnchors;
      flavorAnchors = filterByRestrictions([...new Set(mergedFlavors)], this.restrictionKeys);
      // Don't apply avoid list when mixing cuisines (too restrictive)
      cuisineAvoid = [];
    }
    
    return { cuisineAnchors, cuisineAvoid, flavorAnchors };
  }

  // ─────────────────────────────────────────────────────────────────
  // PUBLIC API: DIETARY CONSTRAINTS
  // ─────────────────────────────────────────────────────────────────

  /**
   * Get formatted dietary preferences string
   * @returns {string}
   */
  getFormattedDietaryPreferences() {
    return this._formatDietaryPreferences(this.dietaryPreferences);
  }

  /**
   * Check if there are active dietary restrictions
   * @returns {boolean}
   */
  hasDietaryRestrictions() {
    return this.restrictionKeys.length > 0;
  }

  /**
   * Get dietary swap examples based on restrictions
   * @returns {string}
   */
  getDietarySwapExamples() {
    const swaps = [];
    
    if (this.restrictionKeys.includes('noDairy')) {
      swaps.push('butter → olive oil, cream → coconut cream, cheese → nutritional yeast');
    }
    if (this.restrictionKeys.includes('vegetarian')) {
      swaps.push('ground beef → lentils, chicken → chickpeas, bacon → smoked tofu');
    }
    if (this.restrictionKeys.includes('vegan')) {
      swaps.push('eggs → flax eggs, honey → maple syrup, milk → oat milk');
    }
    if (this.restrictionKeys.includes('noGluten') || this.restrictionKeys.includes('glutenFree')) {
      swaps.push('pasta → rice noodles, bread → corn tortilla, flour → cornstarch');
    }
    if (this.restrictionKeys.includes('noNuts')) {
      swaps.push('almonds → sunflower seeds, peanut butter → tahini');
    }
    if (this.restrictionKeys.includes('halal')) {
      swaps.push('pork → chicken/beef/fish/legumes; wine/sherry/beer/mirin/sake → broth, lemon juice, vinegar, herbs, or spices');
    }
    
    return swaps.length > 0 ? swaps.join('; ') : '';
  }

  /**
   * Build dietary constraint block for prompts
   * @param {string} [supermarketName] - Name of the supermarket for ingredient alternatives
   * @returns {string} Dietary constraint block
   */
  getDietaryConstraintBlock(supermarketName = 'regular supermarkets') {
    if (!this.hasDietaryRestrictions()) return '';
    
    const formattedPrefs = this.getFormattedDietaryPreferences();
    const swapExamples = this.getDietarySwapExamples();
    const forbiddenIngredients = getForbiddenIngredientsText(this.restrictionKeys, 30);
    
    let block = `
DIETARY RESTRICTIONS (HIGHEST PRIORITY — STRICT BAN):
- Dietary preferences: ${formattedPrefs}`;

    // Add explicit forbidden ingredients list if available
    if (forbiddenIngredients) {
      block += `
- FORBIDDEN INGREDIENTS (DO NOT USE ANY OF THESE):
  ${forbiddenIngredients}`;
    }

    block += `
- CRITICAL: Inspirations may include restricted items. SWAP forbidden ingredients with allowed ${supermarketName} alternatives.
${swapExamples ? `- Swap examples: ${swapExamples}` : ''}
- Ensure forbidden items do NOT appear in title, description, or ingredients.`;

    return block;
  }

  // ─────────────────────────────────────────────────────────────────
  // PUBLIC API: INSPIRATIONS
  // ─────────────────────────────────────────────────────────────────

  /**
   * Get recipe inspirations for a meal type
   * @param {string} mealType - 'breakfast', 'lunch', or 'dinner'
   * @param {Object} [options] - Options
   * @param {string} [options.excludeRecipe] - Recipe title to exclude
   * @param {number} [options.count=4] - Number of inspirations to return
   * @returns {Array} Array of inspiration strings
   */
  getInspirations(mealType, options = {}) {
    const { excludeRecipe = '', count = 4 } = options;
    const mealTypeKey = mealType.toLowerCase();

    // Inspirations may be plain strings or {title, formatGroup, effort} objects.
    // These helpers normalize access without breaking legacy string format.
    const getTitle = (item) => (typeof item === 'string' ? item : (item?.title || ''));
    const getGroup = (item) => (typeof item === 'object' && item?.formatGroup) ? item.formatGroup : null;
    const hasGroups = (arr) => Array.isArray(arr) && arr.some(getGroup);
    // Effort tag (mirrors promptGenerators.js): effort:"involved" marks a dish
    // that needs notable active work; absent == easy (hands-off baseline).
    const isInvolved = (item) => (typeof item === 'object' && item?.effort === 'involved');
    const effortRank = (item) => (isInvolved(item) ? 1 : 0);

    // Effort-bias mode from this meal type's cooking complexity:
    // 'simple' → strict (hands-off only, with fallback); else relaxed (easy-first).
    const complexity = this.cookingStyle?.[mealTypeKey]?.cookingTime || 'standard';
    const effortMode = complexity === 'simple' ? 'strict' : 'relaxed';

    const shuffleArray = (arr) => {
      const out = [...arr];
      for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
      }
      return out;
    };

    // Format-balanced picking: round-robin across distinct formatGroups so a sample of N
    // breakfasts won't all be e.g. toasts. Biases toward easy/hands-off dishes:
    // 'strict' drops involved dishes unless the easy pool can't fill N; 'relaxed'
    // prefers the easy option within each format group. Falls back to plain
    // shuffle for legacy string lists.
    const pickFormatBalanced = (arr, n, mode = effortMode) => {
      if (!arr?.length) return [];

      let pool = arr;
      if (mode === 'strict') {
        const easyPool = arr.filter(item => !isInvolved(item));
        pool = easyPool.length >= n ? easyPool : arr;
      }

      if (!hasGroups(pool)) {
        return shuffleArray(pool)
          .sort((a, b) => effortRank(a) - effortRank(b))
          .slice(0, Math.min(n, pool.length));
      }
      const buckets = new Map();
      for (const item of pool) {
        const g = getGroup(item) || '__none';
        if (!buckets.has(g)) buckets.set(g, []);
        buckets.get(g).push(item);
      }
      const order = shuffleArray(Array.from(buckets.keys()));
      for (const g of order) buckets.set(g, shuffleArray(buckets.get(g)).sort((a, b) => effortRank(a) - effortRank(b)));
      const selected = [];
      while (selected.length < n) {
        let progressed = false;
        for (const g of order) {
          if (selected.length >= n) break;
          const b = buckets.get(g);
          if (b.length) { selected.push(b.shift()); progressed = true; }
        }
        if (!progressed) break;
      }
      return selected;
    };

    // Helper to get inspirations from a specific cuisine
    const getInspirationsByCuisine = (cuisine, subCount = 3) => {
      if (!inspirationsData[cuisine] || !inspirationsData[cuisine][mealTypeKey]) {
        return [];
      }

      let availableInspirations = inspirationsData[cuisine][mealTypeKey];

      // Filter out the excluded recipe (compare via title text — items may be objects)
      if (excludeRecipe) {
        const exclFirstWord = excludeRecipe.toLowerCase().split(' ')[0];
        availableInspirations = availableInspirations.filter(
          item => !getTitle(item).toLowerCase().includes(exclFirstWord)
        );
      }

      // Filter by dietary restrictions (work on titles, keep originals)
      if (this.restrictionKeys.length > 0) {
        const titles = availableInspirations.map(getTitle);
        const allowed = new Set(filterByRestrictions(titles, this.restrictionKeys));
        availableInspirations = availableInspirations.filter(item => allowed.has(getTitle(item)));
      }

      // Format-balanced sample, then return as title strings.
      return pickFormatBalanced(availableInspirations, subCount).map(getTitle);
    };

    if (this.isSingleCuisine || this.isMultiCuisine) {
      // Use selected cuisine(s)
      const allInspirations = [];
      
      this.parsedCuisines.forEach(cuisine => {
        allInspirations.push(...getInspirationsByCuisine(cuisine, Math.ceil(count / this.parsedCuisines.length)));
      });
      
      return this._getRandomItems(allInspirations, count);
    }
    
    // Surprise Me mode - use country's preferred cuisines
    return this._getSurpriseMeInspirations(mealTypeKey, count, getInspirationsByCuisine);
  }

  /**
   * Get inspirations for Surprise Me mode based on country preferences
   * @private
   */
  _getSurpriseMeInspirations(mealTypeKey, count, getInspirationsByCuisine) {
    const preferredCuisines = this._normalizePreferredCuisines(
      this.countryData?.preferredCuisines || []
    );
    
    if (preferredCuisines.length === 0) {
      // Fallback to American
      return getInspirationsByCuisine('American', count);
    }
    
    // Get inspirations from top weighted cuisines
    const allInspirations = [];
    const sortedCuisines = [...preferredCuisines].sort((a, b) => b.w - a.w);
    
    const topCuisines = sortedCuisines.slice(0, 3);
    topCuisines.forEach(cuisine => {
      allInspirations.push(...getInspirationsByCuisine(cuisine.v, 2));
    });
    
    return this._getRandomItems(allInspirations, count);
  }

  /**
   * Normalize preferred cuisines to { v, w } format
   * @private
   */
  _normalizePreferredCuisines(rawPreferences) {
    if (!rawPreferences || rawPreferences.length === 0) return [];
    
    return rawPreferences.map((item) => {
      if (typeof item === 'string') {
        const match = item.match(/^(.+?)\s*\((\d+)%\)$/);
        if (match) {
          return { v: match[1].trim(), w: parseInt(match[2], 10) };
        } else {
          return { v: item.trim(), w: Math.round(100 / rawPreferences.length) };
        }
      } else if (typeof item === 'object' && item.v && item.w) {
        return item;
      }
      return null;
    }).filter(Boolean);
  }

  /**
   * Get full inspiration sets for all meal types (for main meal plan generation)
   * @returns {Object} { breakfast: [], lunch: [], dinner: [] }
   */
  getAllInspirations() {
    return {
      breakfast: this.getInspirations('breakfast', { count: 6 }),
      lunch: this.getInspirations('lunch', { count: 6 }),
      dinner: this.getInspirations('dinner', { count: 6 })
    };
  }

  // ─────────────────────────────────────────────────────────────────
  // PUBLIC API: CUISINE DISTRIBUTION
  // ─────────────────────────────────────────────────────────────────

  /**
   * Get cuisine distribution string for prompts
   * For selected cuisines: returns "all meals from X cuisine(s)"
   * For Surprise Me: calculates weighted distribution
   * @returns {Object} { cuisineDistribution: string }
   */
  getCuisineDistribution() {
    if (!this.isSurpriseMe) {
      return { cuisineDistribution: `all meals from ${this.selectedCuisine} cuisine(s)` };
    }
    
    const rawPreferredCuisines = this.countryData?.preferredCuisines || [];
    const cuisinesWithWeights = this._normalizePreferredCuisines(rawPreferredCuisines);
    
    if (cuisinesWithWeights.length === 0) {
      return { cuisineDistribution: 'varied cuisines' };
    }
    
    // Convert percentages to exact meal counts
    const totalWeight = cuisinesWithWeights.reduce((sum, c) => sum + c.w, 0);
    const cuisineAllocations = cuisinesWithWeights.map(c => ({
      name: c.v,
      exactCount: (c.w / totalWeight) * this.totalMeals,
      baseCount: Math.floor((c.w / totalWeight) * this.totalMeals),
      remainder: ((c.w / totalWeight) * this.totalMeals) % 1
    }));
    
    // Distribute remaining meals by largest remainder
    let remaining = this.totalMeals - cuisineAllocations.reduce((sum, c) => sum + c.baseCount, 0);
    const sortedByRemainder = [...cuisineAllocations].sort((a, b) => b.remainder - a.remainder);
    
    for (let i = 0; i < remaining && i < sortedByRemainder.length; i++) {
      sortedByRemainder[i].baseCount += 1;
    }
    
    // Build the distribution string with exact counts
    const distributionParts = cuisineAllocations
      .filter(c => c.baseCount > 0)
      .map(c => `${c.name}: ${c.baseCount} meal${c.baseCount > 1 ? 's' : ''}`)
      .join(', ');
    
    return { cuisineDistribution: distributionParts };
  }

  // ─────────────────────────────────────────────────────────────────
  // PUBLIC API: ACCESSORS
  // ─────────────────────────────────────────────────────────────────

  /**
   * Get country data
   * @returns {Object}
   */
  getCountryData() {
    return this.countryData;
  }

  /**
   * Get supermarket name for the country
   * @returns {string}
   */
  getSupermarketName() {
    return this.countryData?.supermarket || 
           (this.countryData?.supermarkets && this.countryData.supermarkets[0]) || 
           'regular supermarkets';
  }

  /**
   * Get measurement system info
   * @returns {Object} { isMetric, measurementUnits }
   */
  getMeasurementSystem() {
    // Countries that use imperial system
    const imperialCountries = ['USA', 'United States', 'US'];
    const isMetric = !imperialCountries.includes(this.country);
    
    return {
      isMetric,
      measurementUnits: isMetric ? 'g, ml, cup, tbsp, unit' : 'oz, lb, fl oz, cup, tbsp, tsp, unit'
    };
  }

  /**
   * Get restriction keys
   * @returns {Array}
   */
  getRestrictionKeys() {
    return this.restrictionKeys;
  }

  /**
   * Get parsed cuisines
   * @returns {Array}
   */
  getParsedCuisines() {
    return this.parsedCuisines;
  }

  // ─────────────────────────────────────────────────────────────────
  // PUBLIC API: COOKING TIME LIMITS
  // ─────────────────────────────────────────────────────────────────

  /**
   * Get cooking time limit for a specific meal type based on cooking style
   * Uses the same logic as promptGenerators.js
   * @param {string} mealType - 'Breakfast', 'Lunch', or 'Dinner'
   * @returns {number} Total cooking-time ceiling in minutes (active cap + passive bonus)
   */
  getCookingTimeLimit(mealType) {
    const mealTypeKey = mealType.toLowerCase();
    
    // Default cooking style if not provided
    const defaultComplexity = 'standard';
    
    // Get complexity from cooking style or use default
    let complexity = defaultComplexity;
    if (this.cookingStyle && this.cookingStyle[mealTypeKey]) {
      const mealStyle = this.cookingStyle[mealTypeKey];
      complexity = typeof mealStyle === 'object' 
        ? (mealStyle.cookingTime || defaultComplexity)
        : defaultComplexity;
    }
    
    // Get time limit based on meal type and complexity
    const limits = this.COOKING_TIME_LIMITS[mealTypeKey];
    if (!limits) {
      // Fallback defaults (active cap) + passive bonus = total ceiling
      const fallback = mealType === 'Breakfast' ? 15 : mealType === 'Lunch' ? 35 : 45;
      return fallback + getPassiveCookingBonus(mealType);
    }

    // Return the TOTAL ceiling (active cap + passive oven/simmer bonus) so
    // alternative recipes may also use genuinely passive oven/bake time.
    return (limits[complexity] || limits.standard) + getPassiveCookingBonus(mealTypeKey);
  }

  /**
   * Return a human-readable complexity description for a meal type, based on
   * the cooking style configured for this PromptBuilder instance.
   * Returns null when the complexity is 'standard' (no instruction needed —
   * the model's default behaviour already matches).
   * Descriptions are intentionally identical to those in promptGenerators.js.
   * @param {string} mealType - 'Breakfast', 'Lunch', or 'Dinner'
   * @returns {string|null}
   */
  getComplexityDescription(mealType) {
    const key = String(mealType).trim().toLowerCase();
    const complexity = this.cookingStyle?.[key]?.cookingTime || 'standard';

    const descriptions = {
      simple: {
        breakfast: 'fast and effortless — no-cook assembly, toasting, or boiling; minimal hands-on attention',
        lunch: 'fast and effortless — assembly, tossed salad/bowl/wrap, canned legumes, ready grains, a quick oven/sheet-pan tray, or short one-pan cooking; minimal hands-on work',
        dinner: 'fast and effortless — sheet-pan, oven-bake, or one-pot; mostly passive with minimal hands-on work',
      },
      standard: {
        breakfast: 'still easy and hands-off, with a little more cooking than the quickest option; simple common techniques, no fussy multi-step components',
        lunch: 'still easy and hands-off, with a bit more cooking time; favour oven/sheet-pan roasting or one-pot simmering, or simple assembly with a tasty sauce/side — never active stovetop juggling',
        dinner: 'still easy and hands-off, with a bit more cooking time; favour roasting, baking, sheet-pan or one-pot meals over constant stirring or multiple active pans',
      },
      advanced: {
        breakfast: 'still easy and hands-off — the extra time only buys richer flavour, never chef technique or stressful multitasking',
        lunch: 'still easy and hands-off — use the extra time for deeper flavour via mostly-passive methods (a longer roast/bake or one-pot simmer), NOT more active steps or multi-pan juggling',
        dinner: 'still easy and hands-off — use the extra time for deeper flavour via slow oven-braising, roasting, or one-pot simmering; long UNATTENDED cooking, not more active work',
      },
    };
    return descriptions[complexity]?.[key] || descriptions.standard[key] || null;
  }

  /**
   * Get all cooking time limits for breakfast, lunch, dinner
   * @returns {Object} { breakfast: number, lunch: number, dinner: number }
   */
  getAllCookingTimeLimits() {
    return {
      breakfast: this.getCookingTimeLimit('Breakfast'),
      lunch: this.getCookingTimeLimit('Lunch'),
      dinner: this.getCookingTimeLimit('Dinner')
    };
  }

  /**
   * Check if a meal type is set to cook for 2 days (batch cooking)
   * @param {string} mealType - 'Breakfast', 'Lunch', or 'Dinner'
   * @returns {boolean}
   */
  isForTwoDays(mealType) {
    const mealTypeKey = mealType.toLowerCase();
    
    if (!this.cookingStyle || !this.cookingStyle[mealTypeKey]) {
      return false;
    }
    
    const mealStyle = this.cookingStyle[mealTypeKey];
    return typeof mealStyle === 'object' 
      ? !!mealStyle.cookFor2Days 
      : mealStyle === 2;
  }
}

export default PromptBuilder;
